/* eslint-disable react/prop-types */
import React, {
  useState, useEffect, useRef, useCallback, useMemo,
} from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Input,
  Modal,
  message,
  notification,
  Card,
  Alert,
  Typography,
  Progress,
  Tabs,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  RedoOutlined,
  DownOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { formatReceived } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalConfirmation from 'components/ModalConfirmation';
import ModalDelete from 'components/ModalDelete';
import ModalErroresControls from 'components/ModalErroresControls';
import Select from 'components/Select';
import { getEstadosGlobales } from 'api/catalogos/index';
import {
  getNivelesDeEstudios,
  postNivelDeEstudio,
  deleteNivelDeEstudio,
  patchNivelDeEstudio,
  getNivelDeEstudio,
  autorizarNivelDeEstudio,
} from 'api/catalogos/nivelesDeEstudios';
import API from 'utils/api';
import { checkToken } from 'api/utils';
import { NOMBRES_PATTERN } from 'utils/patterns';
import {
  ESTATUS_ACTIVO,
  ESTATUS_INACTIVO,
  ESTATUS_AUTORIZADO,
  ESTATUS_CANCELADO,
} from 'utils/constants';
import {
  autorizarSBCDetalles,
  getSBCDetalle,
  getSBCDetalles,
  patchSBCDetalle,
  postSBCDetalle,
} from 'api/catalogos/SBCDetalle';
import { getSBCDetallesPercepcion } from 'api/catalogos/SBCDetallePercepcion';
import {
  autorizarSBvariables,
  deleteSBCvariable,
  getProgresoCalculoSBC,
  getSBCvariable,
  getSBCvariables,
  patchSBCvariable,
  postSBCvariable,
} from 'api/catalogos/SBCVariable';
import { getSBCVariableErrores } from 'api/catalogos/SBCVariableErrores';
import currencyFormatter from 'utils/currencyFormatter';
import { getPeriodosFiscales } from 'api/configuraciones/periodosFiscales';
import { getTiposDeNominasEmpleados } from 'api/nominas/catalogos/tiposNominasEmpleados';
import { getSBCBimestres } from 'api/catalogos/SBCBimentre';
import { getPercepciones } from 'api/nominas/percepciones/percepciones';
import { getEmpleadosOptimized } from 'api/estructuraOrganizacional/empleados';
import PeriodoFiscal from '../Otros/PeriodoFiscal';

export const sbcVariablePermission = {
  permissionModel: 'salariobasedecotizacionvariable',
  catalogsModels: [],
  tabsModels: [],
};

const { Text } = Typography;

// `todos: null` -> no se envía `estados_globales` y el backend regresa los
// cálculos de todos los estatus (mismo patrón que Descuentos.jsx).
const TAB_ESTADOS_SBC = {
  todos: null,
  activos: ESTATUS_ACTIVO,
  autorizados: ESTATUS_AUTORIZADO,
  cancelados: ESTATUS_CANCELADO,
};

const SBCVariable = ({ permission }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [tabListaActiva, setTabListaActiva] = useState('todos');
  const [visibleAutorizar, setVisibleAutorizar] = useState(false);
  const [visibleCancelar, setVisibleCancelar] = useState(false);
  const [visibleReactivar, setVisibleReactivar] = useState(false);
  const [visibleEliminar, setVisibleEliminar] = useState(false);
  const [visibleConfirmRecalcular, setVisibleConfirmRecalcular] = useState(false);
  const [selectedData, setSelectedData] = useState({});
  const [visibleConfirmInactivo, setVisibleConfirmInactivo] = useState(false);
  const [disableFields, setDisableFields] = useState(false);
  const [dataEmpleados, setDataEmpleados] = useState([]);
  const [dataPeriodos, setDataPeriodos] = useState([]);
  const [periodosFiscales, setPeriodosFiscales] = useState([]);
  const [tiposNominasEmpleados, setTiposNominasEmpleados] = useState([]);
  const [bimestres, setBimestres] = useState([]);
  const [pageActual, setPageActual] = useState(1);
  const [formMain] = Form.useForm();
  const [formModal] = Form.useForm();
  const [percepciones, setPercepciones] = useState([]);
  const [errores, setErrores] = useState([]);
  const [visibleErrores, setVisibleErrores] = useState(false);
  const [empleados, setEmpleados] = useState([]);

  const [showTables, setShowTables] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [sbcEnProcesoId, setSbcEnProcesoId] = useState(null);
  const [progresoCalculo, setProgresoCalculo] = useState({ calculadas: 0, total: 0 });

  const formInstances = [
    form,
  ];

  const baseURI = '/nominas/salario-base-de-cotizacion-variable';

  // Texto legible "Tipo de Nómina Empleado + Periodo Fiscal + Bimestre" de un
  // registro SBC Variable, para reemplazar el `#id` desnudo en los modales de
  // confirmación de Autorizar/Cancelar/Reactivar/Recalcular y en las
  // notificaciones/progreso de Cálculo y Recálculo.
  const describirRegistroSBC = (registro, idFallback) => {
    if (!registro) return `#${idFallback}`;
    const tipo = tiposNominasEmpleados.find(
      (e) => e.id === registro.tipo_de_nomina_empleado,
    )?.descripcion;
    const periodo = periodosFiscales.find(
      (e) => e.id === registro.periodo_fiscal,
    )?.descripcion;
    const bimestre = bimestres.find((e) => e.id === registro.bimestre);
    const bimestreTexto = bimestre
      ? `${bimestre.nombre} (${bimestre.periodo_descripcion})`
      : null;
    const partes = [tipo, periodo, bimestreTexto].filter(Boolean);
    return partes.length ? partes.join(' | ') : `#${registro.id ?? idFallback}`;
  };

  const describirSBC = (sbcId) => (
    describirRegistroSBC(data?.results?.find((e) => e.id === sbcId) || selectedData, sbcId)
  );

  const fetchData = async (page = 1, q = null, tabKey = tabListaActiva) => {
    setLoading(true);
    const estadosGlobales = TAB_ESTADOS_SBC[tabKey];
    const _data = await API.get(baseURI, {
      params: { page, ...(estadosGlobales ? { estados_globales: estadosGlobales } : {}), ...q },
    });
    const { results } = _data.data;
    setData({ ..._data.data, results });
    setLoading(false);
  };

  const onChangeTabLista = (newTab) => {
    setTabListaActiva(newTab);
    setSelectedRowKeys([]);
    setSelectedData({});
    fetchData(1, null, newTab);
  };

  const fetchPaginated = async (page = 1, q = null) => {
    try {
      setLoading(true);
      const estadosGlobales = TAB_ESTADOS_SBC[tabListaActiva];
      const response = await API.get(baseURI, {
        params: { page, ...(estadosGlobales ? { estados_globales: estadosGlobales } : {}), ...q },
      });
      const { results } = response.data;
      setSelectedRowKeys([]);
      setPageActual(page);
      setData({ ...response.data, results });
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _bimestres,
        _tiposNominasEmpleados,
        _periodosFiscales,
        _data,
        _percepciones,
        _empleados,
      ] = await Promise.all([
        getEstadosGlobales(),
        getSBCBimestres(),
        getTiposDeNominasEmpleados({ estados_globales: ESTATUS_AUTORIZADO }),
        getPeriodosFiscales({ estados_globales: ESTATUS_AUTORIZADO }),
        getSBCvariables(),
        getPercepciones(),
        getEmpleadosOptimized(),
      ]);
      // El detalle por Empleado/Percepción (dataEmpleados/dataPeriodos) ya NO
      // se trae aquí sin filtrar: antes esto pedía TODA la tabla de detalle
      // (miles de filas de todos los cálculos históricos) y, al venir
      // paginada por DRF a 10 filas, mostraba 10 registros arbitrarios en
      // vez de los del cálculo abierto. Ahora se trae scoped por
      // `salario_base_cotizacion_variable` dentro de onClickEdit/
      // refreshSBCVariable, que es cuando realmente se sabe qué cálculo se
      // está viendo.
      const { results } = _data;
      if (mounted) {
        setEstados(_estados);
        setBimestres(_bimestres);
        setTiposNominasEmpleados(_tiposNominasEmpleados);
        setPeriodosFiscales(_periodosFiscales);
        setPercepciones(_percepciones?.results || []);
        setData({ ..._data, results });
        setEmpleados(Array.isArray(_empleados) ? _empleados : _empleados?.results || []);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
    }, [])

  const pollingRef = useRef(null);

  // Reinicia el contador de progreso cada vez que arranca un cálculo/recálculo,
  // igual que el patrón de "Calculando Nómina..." en CalculoDeNominas.jsx.
  useEffect(() => {
    if (isPolling) {
      setProgresoCalculo({ calculadas: 0, total: 0 });
    }
  }, [isPolling]);

  useEffect(() => {
  //  Función auxiliar: refresca los datos al finalizar el cálculo
    const refreshSBCVariable = async (id) => {
      try {
        const _sbc = await getSBCvariable(id);
        const [_detallesEmpleados, _detallesPeriodos, _errores] = await Promise.all([
          getSBCDetalles({ salario_base_cotizacion_variable: id }),
          getSBCDetallesPercepcion({ salario_base_cotizacion_variable: id }),
          getSBCVariableErrores({ salario_base_cotizacion_variable: id }),
        ]);

        setDataEmpleados(formatReceived(_detallesEmpleados || []));
        setDataPeriodos(formatReceived(_detallesPeriodos || []));
        // Historial de Errores: se actualiza justo cuando el cálculo
        // realmente terminó, igual que onCalculoTerminado en Nómina.
        setErrores(_errores || []);
        setLoading(false); // ⏹️ Detiene el spinner principal
      } catch (error) {
        onError(error, setLoading);
      }
    };

    //  Iniciar el polling solo si hay cálculo en proceso
    if (isPolling && sbcEnProcesoId) {
      const idEnCurso = sbcEnProcesoId;
      pollingRef.current = setInterval(async () => {
        try {
          // Endpoint liviano: cuenta SalarioBaseDeCotizacionVariableDetalle
          // ya guardados en tiempo real (mismo patrón que
          // getProgresoCalculo en el cálculo de Nómina)
          const progreso = await getProgresoCalculoSBC(idEnCurso);
          if (!progreso) return;

          setProgresoCalculo((prev) => ({
            calculadas: progreso.calculadas ?? prev.calculadas,
            total: progreso.total || prev.total,
          }));

          //  Caso 1: Cálculo finalizado y exitoso
          if (progreso.terminado) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
            setIsPolling(false);
            setSbcEnProcesoId(null);
            setProgresoCalculo({ calculadas: 0, total: 0 });

            const sbcData = await getSBCvariable(idEnCurso);
            notification.success({
              message: 'Cálculo SBC Variable completado',
              description: `El cálculo ${describirRegistroSBC(sbcData)} ha finalizado correctamente en ${sbcData?.tiempo_de_calculo || ''}.`,
              placement: 'topRight',
              duration: 10,
            });

            await refreshSBCVariable(sbcData.id);
          }
        // Caso 2: Todavía en proceso → no hace nada y sigue esperando
        } catch (error) {
          // Error transitorio (p. ej. el worker aún no crea la fila): se
          // ignora y se reintenta en el siguiente tick, igual que el
          // cálculo de Nómina.
        }
      }, 5000); // ↻ cada 5 segundos
    }

    //  Limpieza del intervalo cuando el componente se desmonta
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [isPolling, sbcEnProcesoId]);

  const onCancel = () => {
    setVisible(false);
    setVisibleAutorizar(false);
    setVisibleCancelar(false);
    setVisibleReactivar(false);
    setVisibleEliminar(false);
    setDisableFields(false);
    setSelectedRowKeys([]);
    setSelectedData({});
    // Evita que el detalle de un cálculo quede visible brevemente al abrir
    // otro distinto (o "Agregar") mientras llega la respuesta scoped nueva.
    setDataEmpleados([]);
    setDataPeriodos([]);
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;

    // 1. CORRECCIÓN: Buscar en 'data.results' (la data de la tabla principal)
    const match = responseData || data.results.find((item) => item.id === key);

    // Agregamos una validación por si no encuentra el 'match'
    if (!match) {
      message.error('No se pudo encontrar el registro seleccionado.');
      setLoading(false);
      return;
    }

    // Esta llamada es correcta, trae los datos frescos del registro
    const _sbcRegistro = await getSBCvariable(match.id);

    // Historial de Errores del cálculo (mismo patrón que CalculoDeNominas.jsx)
    const _errores = await getSBCVariableErrores({
      salario_base_cotizacion_variable: match.id,
    });
    setErrores(_errores || []);

    // Detalle por Empleado/Percepción, scoped al cálculo que se está
    // abriendo (ver nota en fetchAll: traerlo sin filtrar mostraba 10 filas
    // arbitrarias de toda la tabla histórica). Al filtrar por
    // `salario_base_cotizacion_variable` sin mandar `page`, el backend ya no
    // pagina (CustomPageNumberPagination) y regresa un arreglo plano.
    const [_detallesEmpleados, _detallesPeriodos] = await Promise.all([
      getSBCDetalles({ salario_base_cotizacion_variable: match.id }),
      getSBCDetallesPercepcion({ salario_base_cotizacion_variable: match.id }),
    ]);
    setDataEmpleados(formatReceived(_detallesEmpleados || []));
    setDataPeriodos(formatReceived(_detallesPeriodos || []));

    setTimeout(() => {
      if (_sbcRegistro) {
        // 2. CORRECCIÓN: Usar 'formMain'
        formMain.resetFields();

        setTimeout(() => {
          // 3. CORRECCIÓN: Usar 'formMain'
          formMain.setFieldsValue(formatReceived(_sbcRegistro));

          setDisableFields(true);
          setVisible(true);

          // Tu lógica existente para mostrar las tablas (esto estaba bien)
          if (_sbcRegistro?.fecha_final_calculo || _sbcRegistro?.calculo_exitoso) {
            setShowTables(true);
            setIsPolling(false); // No hacer polling, ya está calculado
            setSbcEnProcesoId(null);
          } else {
            // El cálculo sigue en curso (p. ej. se cerró el Detalle y se
            // volvió a abrir antes de que terminara): retoma el polling en
            // vez de dejar la vista sin ningún indicador de progreso.
            setShowTables(false);
            setSbcEnProcesoId(_sbcRegistro.id);
            setIsPolling(true);
          }
        });
      }
    });
    setLoading(false);
  };

  const onFinish = async (_values, _continue = true) => {
    try {
      setLoading(true);
      await formMain.validateFields();

      const values = {
        ...formMain.getFieldsValue(),
      };

      // Crear o recalcular el SBC Variable
      const saved = await postSBCvariable(values);

      if (saved) {
        if (_continue) {
        // Refrescamos la tabla principal
          await fetchData();

          // Guardamos el ID del cálculo en proceso
          setSelectedRowKeys([saved.id]);
          setSbcEnProcesoId(saved.id);

          setShowTables(true);
          // El registro ya existe y el cálculo ya se disparó en el backend;
          // no se puede volver a "crear" sobre el mismo formulario.
          setDisableFields(true);
          // Limpia el Historial de Errores de un cálculo previo (no aplica
          // aquí al ser una creación nueva, pero mantiene el patrón de
          // Nómina: nunca arrancar un cálculo con errores viejos visibles).
          setErrores([]);
          // Mostramos notificación
          notification.info({
            message: 'Cálculo en Proceso',
            description: `El cálculo SBC Variable ${describirRegistroSBC(saved)} ha comenzado. Se le notificará al finalizar.`,
            placement: 'topRight',
            duration: 10,
          });

          //  Activamos el polling (Guardar y Continuar: el Detalle se queda
          //  abierto para ver el progreso, no se cierra aquí)
          setIsPolling(true);
        } else {
        // Si no se desea continuar, simplemente cerramos y actualizamos
          onCancel();
          await fetchData();
        }
      }

      //  Quitamos el spinner global; el polling controla el resto
      setLoading(false);
    } catch (err) {
      onError(err, setLoading, formInstances);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setSelectedData({ ...record });
    form.setFieldsValue(record);
  };

  const handleKeyPress = (e) => {
    if (/\d/.test(e.key)) {
      e.preventDefault();
    }
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
  //  Limpieza total antes de abrir el formulario
    formMain.resetFields();
    form.resetFields();
    setSelectedRowKeys([]); // Desmarcar selección previa
    setSelectedData({}); // Quitar datos del registro anterior
    setDisableFields(false); // Habilitar los campos
    setShowTables(false); //  Ocultar las tablas
    setIsPolling(false); //  Detener cualquier polling anterior
    setSbcEnProcesoId(null); // Limpiar ID previo de cálculo
    setVisible(true); // Abrir modo “Agregar”
  };

  const onConfirmAutorizar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede autorizar un Nivel de Estudios ya autorizado');
        return;
      }
      const saved = await autorizarSBvariables(key);
      if (saved) {
        await fetchData();
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onConfirmCancelar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede cancelar un Cálculo SBC Variable ya cancelado');
        return;
      }
      const saved = await patchSBCvariable(key, { estados_globales: ESTATUS_CANCELADO });
      if (saved) {
        await fetchData();
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onConfirmReactivar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede reactivar un Cálculo SBC Variable ya activo');
        return;
      }
      const saved = await patchSBCvariable(key, { estados_globales: ESTATUS_ACTIVO });
      if (saved) {
        await fetchData();
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onConfirmEliminar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        return;
      }
      const deleted = await deleteSBCvariable(key);
      if (deleted) {
        await fetchData();
        setVisibleEliminar(false);
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onErrors = () => {
    setVisibleErrores(true);
  };

  const onClickRecalcular = async () => {
    // 1. Activar el spinner global
    setLoading(true);
    try {
      const [key] = selectedRowKeys;
      if (!key) {
        message.error('No hay un registro seleccionado para recalcular.');
        setLoading(false);
        return;
      }

      const url = `nominas/salario-base-de-cotizacion-variable-calculo/${key}/`;

      await API.put(url, {});
      setSbcEnProcesoId(key);
      // Limpia el Historial de Errores del cálculo anterior antes de ver el
      // progreso del recálculo (mismo patrón que Nómina).
      setErrores([]);

      notification.info({
        message: 'Recálculo en Proceso',
        description: `El recálculo SBC Variable ${describirSBC(key)} ha comenzado. Se le notificará al finalizar.`,
        placement: 'topRight',
        duration: 10,
      });

      // 4. Activamos el polling
      setIsPolling(true);

      // 5. Quitamos el spinner global. El polling local (isPolling) toma el control.
      setLoading(false);
    } catch (err) {
      // El manejador de error se encargará de setLoading(false)
      onError(err, setLoading);
    }
  };
  const columns = [
    {
      titleText: 'Tipo de Nómina',
      dataIndex: 'tipo_de_nomina_empleado',
      key: 'tipo_de_nomina_empleado',
      width: 200,
      render: (val) => {
        const tipo = tiposNominasEmpleados.find((e) => e.id === val);
        return tipo ? tipo.descripcion : `ID ${val}`;
      },
    },
    {
      titleText: 'Periodo Fiscal',
      dataIndex: 'periodo_fiscal',
      key: 'periodo_fiscal',
      width: 200,
      render: (val) => {
        const periodo = periodosFiscales.find((e) => e.id === val);
        return periodo ? periodo.descripcion : `ID ${val}`;
      },
    },
    {
      titleText: 'Bimestre',
      dataIndex: 'bimestre',
      key: 'bimestre',
      width: 150,
      render: (val) => {
        const bim = bimestres.find((e) => e.id === val);
        return bim ? bim.nombre : `ID ${val}`;
      },
    },
    {
      titleText: 'Meses del Bimestre',
      dataIndex: 'meses_del_bimestre',
      key: 'meses_del_bimestre',
      width: 200,
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 150,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  // Tabla fusionada "SBC Variable por Empleado": cada empleado es una fila
  // "Total" (en negritas) con sus columnas Área/Puesto/Plaza, y debajo --
  // indentadas, vía el soporte de filas anidadas de components/Table
  // (`childrenProp`) -- una subfila de detalle por cada Percepción que
  // contribuyó a su SBC Variable (antes eran dos tablas separadas, sin
  // relación visual entre el total de un empleado y sus percepciones).
  const mergedData = useMemo(() => dataEmpleados.map((detalle) => {
    const percepcionesDelEmpleado = dataPeriodos
      .filter((p) => p.salario_base_cotizacion_variable_detalle === detalle.id)
      // Oculta Percepciones sin ningún importe en el bimestre (p.ej. P210
      // que no le tocó a este empleado) -- son ruido, no aportan nada al
      // detalle. El total del empleado (fila "Total") no se toca: viene
      // precalculado del backend, no se arma sumando estas subfilas.
      .filter((p) => Number(p.total_del_bimestre || 0) !== 0)
      .map((p) => ({
        ...p,
        key: `percepcion-${p.id}`,
        isChild: true,
      }));
    return {
      ...detalle,
      key: `empleado-${detalle.id}`,
      isChild: false,
      children: percepcionesDelEmpleado.length ? percepcionesDelEmpleado : undefined,
    };
  }), [dataEmpleados, dataPeriodos]);

  const columnsMerged = [
    {
      titleText: 'No. Empleado',
      dataIndex: 'empleado',
      key: 'empleado',
      width: 130,
      render: (val, record) => {
        if (record.isChild) return '';
        const emp = empleados.find((e) => e.id === val);
        return emp ? emp.numero_de_empleado : val;
      },
    },
    {
      titleText: 'Nombre del Empleado',
      dataIndex: 'empleado',
      key: 'nombre_empleado',
      width: 220,
      render: (val, record) => {
        if (record.isChild) return '';
        const emp = empleados.find((e) => e.id === val);
        return emp
          ? `${emp.nombre} ${emp.apellido_paterno} ${emp.apellido_materno || ''}`
          : '---';
      },
    },
    {
      titleText: 'Área',
      dataIndex: 'directorio_de_area_clave_y_nombre',
      key: 'directorio_de_area_clave_y_nombre',
      width: 220,
      render: (val, record) => (record.isChild ? '' : val),
    },
    {
      titleText: 'Puesto',
      dataIndex: 'puesto_descripcion',
      key: 'puesto_descripcion',
      width: 200,
      render: (val, record) => (record.isChild ? '' : val),
    },
    {
      titleText: 'Plaza',
      dataIndex: 'plaza_clave_y_descripcion',
      key: 'plaza_clave_y_descripcion',
      width: 200,
      render: (val, record) => (record.isChild ? '' : val),
    },
    {
      titleText: 'Percepción',
      dataIndex: 'percepcion_imss',
      key: 'percepcion',
      width: 250,
      render: (val, record) => {
        if (!record.isChild) return 'Total';
        // El backend ya resuelve "Clave - Descripción" directo del FK
        // (percepcion_clave_y_descripcion, ver
        // SalarioBaseDeCotizacionVariableDetallePercepcionModelSerializer)
        // incluso si la Percepción referenciada ya fue dada de baja y no
        // aparece en el catálogo de Percepciones IMSS activas -- usarlo
        // directo evita el "ID <n>" que daba el lookup contra ese catálogo.
        return record.percepcion_clave_y_descripcion || `ID ${val}`;
      },
    },
    {
      titleText: 'Periodo 1',
      dataIndex: 'periodo_uno',
      key: 'periodo_uno',
      width: 120,
      align: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: 'Periodo 2',
      dataIndex: 'periodo_dos',
      key: 'periodo_dos',
      width: 120,
      align: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: 'Periodo 3',
      dataIndex: 'periodo_tres',
      key: 'periodo_tres',
      width: 120,
      align: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: 'Periodo 4',
      dataIndex: 'periodo_cuatro',
      key: 'periodo_cuatro',
      width: 120,
      align: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: 'Total Bimestre',
      dataIndex: 'total_del_bimestre',
      key: 'total_del_bimestre',
      width: 150,
      align: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: 'Días Bimestre',
      dataIndex: 'no_de_dias',
      key: 'no_de_dias',
      width: 100,
      render: (val, record) => (record.isChild ? '' : val),
    },
    {
      titleText: 'SBC Variable',
      dataIndex: 'sbc',
      key: 'sbc',
      width: 150,
      align: 'right',
      render: (val, record) => (record.isChild ? '' : currencyFormatter(val || 0)),
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    nombre: [
      requiredRule,
      {
        pattern: NOMBRES_PATTERN,
        message: 'Ingrese un nombre válido',
      },
    ],
  };

  // Botones de acción reutilizados entre tabs: el permiso y el `disabled`
  // dinámico (basado en el estado real del registro seleccionado) no cambian
  // por tab -- solo cambia cuáles de los tres aparecen en cada uno, igual que
  // en Descuentos.jsx.
  const accionAutorizar = {
    onClick: () => setVisibleAutorizar(true),
    permission: { model: 'salariobasedecotizacionvariable', action: 'autorizar' },
    text: 'Autorizar',
    icon: <CheckCircleOutlined />,
    disabledBySelection: false,
    className: 'success',
    disabled:
    !selectedRowKeys?.length
    || data?.results?.find(
      (e) => e.id === selectedRowKeys[0],
    )?.estados_globales === ESTATUS_AUTORIZADO
    // No se puede Autorizar un cálculo sin registros de "SBC
    // Variable por Empleado" (ver tiene_detalles en el serializer).
    || !data?.results?.find(
      (e) => e.id === selectedRowKeys[0],
    )?.tiene_detalles,
  };

  const accionCancelar = {
    permission: { model: 'salariobasedecotizacionvariable', action: 'cancelar' },
    onClick: () => setVisibleCancelar(true),
    text: 'Cancelar',
    icon: <CloseCircleOutlined />,
    disabledBySelection: false,
    className: 'cancel',
    disabled: !selectedRowKeys?.length
    || data?.results?.find(
      (e) => e.id === selectedRowKeys[0],
    )?.estados_globales === ESTATUS_CANCELADO,
  };

  const accionReactivar = {
    permission: { model: 'salariobasedecotizacionvariable', action: 'reactivar' },
    onClick: () => setVisibleReactivar(true),
    text: 'Reactivar',
    icon: <RedoOutlined />,
    disabledBySelection: false,
    className: 'success',
    disabled:
    !selectedRowKeys?.length
    || data?.results?.find(
      (e) => e.id === selectedRowKeys[0],
    )?.estados_globales === ESTATUS_ACTIVO,
  };

  const renderTablaSBC = (customActions) => (
    <Table
      cols={columns}
      data={data?.results || []}
      pagination={false}
      paginationCallback={fetchPaginated}
      count={data?.count || 0}
      titleTable="Cálculo SBC Variable"
      rowSelection={rowSelection}
      handleOnRowClick={handleOnRowClick}
      controls={{
        onClickAdd,
        onClickEdit: () => onClickEdit(),
        onClickDelete: () => setVisibleEliminar(true),
      }}
      disableDelete={
        data?.results?.find((e) => e.id === selectedRowKeys[0])
          ?.estados_globales === ESTATUS_AUTORIZADO
      }
      customActions={customActions}
      editText="Detalle"
      permission={permission}
    />
  );

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!open ? (
          <Tabs
            activeKey={tabListaActiva}
            onChange={onChangeTabLista}
            items={[
              {
                key: 'todos',
                label: 'Todos',
                children: renderTablaSBC([accionAutorizar, accionCancelar, accionReactivar]),
              },
              {
                key: 'activos',
                label: 'Activos',
                children: renderTablaSBC([accionAutorizar, accionCancelar]),
              },
              {
                key: 'autorizados',
                label: 'Autorizados',
                children: renderTablaSBC([accionCancelar, accionReactivar]),
              },
              {
                key: 'cancelados',
                label: 'Cancelados',
                children: renderTablaSBC([accionReactivar]),
              },
            ]}
          />
        ) : (
          <>
            {/* Si hay errores del cálculo SBC */}
            {errores?.length ? (
              <Alert
                description={(
                  <code>
                    <Text>
                      Ha ocurrido un error durante el cálculo.
                    </Text>
                    <br />
                    Revisa el
                    <Text
                      style={{
                        color: '#CF9660',
                        paddingLeft: '3px',
                        paddingRight: '3px',
                      }}
                    >
                      Historial de Errores
                    </Text>
                    para más información.
                  </code>
              )}
                type="warning"
                showIcon
                closable
                style={{ textAlign: 'center', alignItems: 'center' }}
              />
            ) : null}

            {/*  Formulario principal y tablas */}
            <Card
              className="form-container"
              title={`${
                selectedRowKeys.length ? 'Detalle' : 'Agregar'
              } Cálculo SBC Variable`}
              extra={(
                <FormSubmitControls
                  onFinish={() => onFinish(undefined, true)}
                  onFinishText="Guardar y Continuar"
                  onCancel={onCancel}
                  loading={loading}
                  mutable={!disableFields}
                  onErrors={errores?.length ? onErrors : null}
                />
            )}
              bordered={false}
            >
              <Form
                layout="vertical"
                form={formMain}
                onFinish={() => onFinish()}
                scrollToFirstError
                name="general"
                initialValues={{
                  estados_globales: ESTATUS_ACTIVO,
                }}
              >
                <Row gutter={10}>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="tipo_de_nomina_empleado"
                      label="Tipo de Nómina Empleado"
                      rules={rules.required}
                      hasFeedback
                    >
                      <Select
                        dataSource={tiposNominasEmpleados}
                        allowClear
                        disabled={disableFields || !!selectedRowKeys.length}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="periodo_fiscal"
                      label="Periodo Fiscal"
                      rules={rules.required}
                      hasFeedback
                    >
                      <Select
                        dataSource={periodosFiscales}
                        allowClear
                        disabled={disableFields || !!selectedRowKeys.length}
                        onChange={() => formMain.setFieldValue('bimestre', undefined)}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      noStyle
                      shouldUpdate={(prev, curr) => prev.periodo_fiscal !== curr.periodo_fiscal}
                    >
                      {({ getFieldValue }) => {
                        const periodoFiscalSeleccionado = getFieldValue('periodo_fiscal');
                        const bimestresDelPeriodo = bimestres.filter(
                          (b) => b.periodo_fiscal === periodoFiscalSeleccionado,
                        );
                        return (
                          <Form.Item
                            name="bimestre"
                            label="Bimestre"
                            rules={rules.required}
                            hasFeedback
                          >
                            <Select
                              keyProp="id"
                              dataSource={bimestresDelPeriodo}
                              render={(value) => `${value?.nombre} (${value?.periodo_descripcion})`}
                              allowClear
                              disabled={disableFields
                                || !!selectedRowKeys.length
                                || !periodoFiscalSeleccionado}
                            />
                          </Form.Item>
                        );
                      }}
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="estados_globales"
                      label="Estatus"
                      rules={rules.required}
                      hasFeedback
                    >
                      <Select
                        labelProp="descripcion"
                        keyProp="id"
                        dataSource={estados}
                        activeInactive={
                        selectedData?.estados_globales === ESTATUS_ACTIVO
                      }
                        disabled
                        onChange={(val) => setVisibleConfirmInactivo(val === ESTATUS_INACTIVO)}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item hidden>
                  <Button htmlType="submit" />
                </Form.Item>
              </Form>

              {/* Tabla de detalle: fusiona Empleado + Percepción (filas
                  anidadas expandibles) */}
              {showTables && (
              <Spin spinning={isPolling} tip="Calculando SBC Variable...">
                <Table
                  title="SBC Variable Empleados"
                  cols={columnsMerged}
                  data={mergedData}
                  // El export a Excel solo recorre las filas anidadas
                  // (Percepciones) si recibe el árbol completo por esta prop
                  // -- sin ella, exportaba únicamente las filas "Total" de
                  // cada empleado.
                  customDataArbolado={mergedData}
                  rowKey="key"
                  childrenProp="children"
                  filterNested
                  allowExpand
                  expandable={{
                    expandRowByClick: true,
                    childrenColumnName: 'children',
                    indentSize: 25,
                    defaultExpandAllRows: true,
                    expandIcon: ({ expanded, onExpand, record }) => (record.children && (
                      expanded ? (
                        <DownOutlined onClick={(e) => onExpand(record, e)} />
                      ) : (
                        <RightOutlined onClick={(e) => onExpand(record, e)} />
                      )
                    )),
                  }}
                  customActions={[
                    {
                      onClick: () => setVisibleConfirmRecalcular(true),
                      // Mismo criterio que "Guardar y Recalcular" en el cálculo de
                      // Nómina (CalculosDeNominaEncabezado.jsx): recalcular requiere
                      // el mismo permiso que crear el cálculo.
                      permission: { model: 'salariobasedecotizacionvariable', action: 'add' },
                      text: 'Recalcular',
                      icon: <RedoOutlined />,
                      className: 'info',
                      disabledBySelection: false,
                      // Solo un registro ACTIVO puede recalcularse (el
                      // backend también lo bloquea, ver
                      // salarioBaseCotizacionVariableCalculo en
                      // nominas/views/salarios_base_de_cotizacion_variable.py).
                      disabled: selectedData?.estados_globales !== ESTATUS_ACTIVO,
                    },
                  ]}
                />
              </Spin>
              )}
            </Card>

            {/*  Modal de progreso del Cálculo/Recálculo, con polling (mismo patrón
                que "Calculando Nómina..." en CalculoDeNominas.jsx) */}
            <Modal
              open={isPolling}
              title="Calculando SBC Variable..."
              footer={null}
              closable={false}
              maskClosable={false}
              centered
            >
              <div style={{ textAlign: 'center', padding: '16px 0' }}>
                <Text style={{ display: 'block', marginBottom: 8 }}>
                  {describirSBC(sbcEnProcesoId)}
                </Text>
                <Progress
                  type="circle"
                  percent={
                    progresoCalculo.total > 0
                      ? Math.min(
                        Math.round((progresoCalculo.calculadas / progresoCalculo.total) * 100),
                        99,
                      )
                      : 0
                  }
                  status="active"
                />
                <p style={{ marginTop: 16 }}>
                  {progresoCalculo.calculadas}
                  {' / '}
                  {progresoCalculo.total || '?'}
                  {' Empleados Calculados'}
                </p>
              </div>
            </Modal>

            <ModalConfirmation
              onFinish={() => setVisibleConfirmInactivo(false)}
              ConfirmInactive
              onCancel={() => {
                form.setFieldValue('estados_globales', ESTATUS_ACTIVO);
                setVisibleConfirmInactivo(false);
              }}
              open={visibleConfirmInactivo}
              contentTitle={`Inactivar el cálculo SBC Variable #${selectedRowKeys[0]}`}
              loading={loading}
            />

            {/*  Modal de Errores -- mismo diseño que el Historial de Errores
                de Cálculo de Nómina (CalculosDeNominaEncabezado.jsx). */}
            <Modal
              visible={visibleErrores}
              title={(
                <Row justify="space-between">
                  <Text style={{ fontSize: '1.25rem' }}>
                    Historial de Errores
                  </Text>
                  <ModalErroresControls
                    onCancel={() => setVisibleErrores(false)}
                  />
                </Row>
            )}
              footer={null}
              closable={false}
              width="85%"
            >
              <Table
                cols={[
                  {
                    titleText: 'Descripción de Error',
                    dataIndex: 'error',
                    key: 'error',
                    width: 400,
                  },
                ]}
                data={errores}
                mobileColIndex={0}
              />
            </Modal>
          </>
        )}

        {/*  Modales de Autorizar/Cancelar/Reactivar: se montan siempre, no solo
            dentro de la vista de Detalle (`open`), porque sus botones viven en
            la vista de Lista (`!open`) -- antes estaban anidados junto con los
            modales del Detalle y nunca se abrían desde la Lista. */}
        <ModalConfirmation
          onFinish={onConfirmCancelar}
          ConfirmCancel
          onCancel={() => setVisibleCancelar(false)}
          contentTitle={`cancelar el cálculo SBC Variable ${describirSBC(selectedRowKeys[0])}`}
          open={visibleCancelar}
          loading={loading}
        />

        <ModalConfirmation
          onFinish={onConfirmAutorizar}
          ConfirmAutorize
          onCancel={() => setVisibleAutorizar(false)}
          contentTitle={`Autorizar el cálculo SBC Variable ${describirSBC(selectedRowKeys[0])}`}
          open={visibleAutorizar}
          loading={loading}
        />

        <ModalConfirmation
          onFinish={onConfirmReactivar}
          onFinishText="Reactivar"
          onFinishIcon={<RedoOutlined />}
          onCancel={() => setVisibleReactivar(false)}
          contentTitle={`Reactivar el cálculo SBC Variable ${describirSBC(selectedRowKeys[0])}`}
          open={visibleReactivar}
          loading={loading}
        />

        <ModalDelete
          open={visibleEliminar}
          onDelete={onConfirmEliminar}
          onCancel={() => setVisibleEliminar(false)}
          loading={loading}
          content={`el cálculo SBC Variable ${describirSBC(selectedRowKeys[0])}`}
        />

        <ModalConfirmation
          onFinish={async () => {
            setVisibleConfirmRecalcular(false);
            await onClickRecalcular();
          }}
          onFinishText="Recalcular"
          onFinishIcon={<RedoOutlined />}
          onCancel={() => setVisibleConfirmRecalcular(false)}
          contentTitle={`recalcular el cálculo SBC Variable ${describirSBC(selectedRowKeys[0])}`}
          open={visibleConfirmRecalcular}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default SBCVariable;
